<template>
    <div class="contact-distributions-by-language" :class="{ fullscreen: fullScreen }">
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>

        <div class="chart-wrapper">
            <DoubleBarChart
                :title="title"
                :xAxisData="getXAxisData()"
                :yAxisData="getYAxisData()"
                :series="getSeries()"
                v-if="!tableMode"
            />
            <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        </div>

        <div class="stats">
            <StatCard :title="$t('report.mostUsedLanguage')" :value="getStats().maxLanguage" />
            <StatCard :title="$t('report.leastUsedLanguage')" :value="getStats().minLanguage" />
            <StatCard
                :title="$t('report.totalPersonCount')"
                :value="formatStat(getStats().total).value"
                unit="Kişi"
            />
        </div>
    </div>
</template>

<script>
import moment from "moment";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import DoubleBarChart from "../../Pages/Chatbot/pages/reports/charts/DoubleBarChart.vue";
import { expertToExcel } from "../../../utils/utils";
import fscreen from "fscreen";

const languageNames = {
    tr: "Türkçe",
    en: "İngilizce",
    ar: "Arapça",
    de: "Almanca",
    fr: "Fransızca",
    es: "İspanyolca",
    ru: "Rusça",
    zh: "Çince",
    ja: "Japonca",
    ko: "Korece",
    it: "İtalyanca",
    pt: "Portekizce",
    nl: "Hollandaca",
    pl: "Lehçe",
    vi: "Vietnamca",
    th: "Tayca",
    id: "Endonezce",
    hi: "Hintçe",
    bn: "Bengalce",
    fa: "Farsça",
};

export default {
    name: "ContactDistributionsByLanguage",
    components: {
        IconButton,
        StatCard,
        TableView,
        DoubleBarChart
    },
    props: {
        title: {
            type: String,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        data: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            tableMode: false,
            fullScreen: false,
            cancels: [],
            loadingInstance: null
        };
    },
    methods: {
        formatStat(value) {
            if (isNaN(value) || value === null || value === undefined) {
                return {
                    value: this.$t("report.noEnoughData"),
                    unit: "",
                };
            }
            return {
                value: value,
                unit: this.$t("report.person"),
            };
        },
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".contact-distributions-by-language");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        getCols() {
            return [
                {
                    title: this.$t("report.language"),
                    dataIndex: "language",
                    key: "language",
                },
                {
                    title: this.$t("report.contactCount"),
                    dataIndex: "count",
                    key: "count",
                },
                {
                    title: this.$t("report.approvedCount"),
                    dataIndex: "approvedCount",
                    key: "approvedCount",
                }
            ];
        },
        getRows() {
            return (this.data || []).map(item => ({
                language: languageNames[item.language] || item.language,
                count: item.count,
                approvedCount: item.approvedCount
            }));
        },
        getStats() {
            const items = this.data || [];
            const maxItem = _.maxBy(items, 'count');
            const minItem = _.minBy(items, 'count');
            return {
                maxLanguage: maxItem ? (languageNames[maxItem.language] || maxItem.language) : this.$t("report.noEnoughtData"),
                minLanguage: minItem ? (languageNames[minItem.language] || minItem.language) : this.$t("report.noEnoughtData"),
                total: _.sumBy(items, 'count')
            };
        },
        getYAxisData() {
            const items = this.data || [];
            return {
                type: "category",
                data: items.map(item => languageNames[item.language] || item.language),
                axisLabel: {
                    interval: 0,
                    rotate: 30,
                },
                splitLine: {
                    show: false,
                }
            };
        },
        getXAxisData() {
            const items = this.data || [];
            const max = _.max(items.map(x => x.count)) || 0;

            return {
                type: "value",
                max: max * 1.1,
                minInterval: 1,
                splitNumber: 10,
                splitLine: {
                    show: false,
                },
                axisLabel: {
                    rotate: -30,
                }
            };
        },
        getSeries() {
            const items = this.data || [];
            return [
                {
                    name: this.$t("report.total"),
                    type: "bar",
                    data: items.map(item => item.count),
                    label: {
                        show: true,
                        position: "right"
                    }
                },
                {
                    name: this.$t("report.approved"),
                    type: "bar",
                    data: items.map(item => item.approvedCount),
                    label: {
                        show: true,
                        position: "right"
                    }
                }
            ];
        },
        exportToExcel() {
            const excelData = this.getRows();
            expertToExcel(excelData, `language_distributions_${moment().format("YYYY-MM-DD")}.xlsx`, "Sheet 1");
        }
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".contact-distributions-by-language"),
            name: "wave",
        });
        if (this.loading) {
            this.loadingInstance.show();
        }
    },
    created() {
        const fullscreenCallback = () => {
            this.fullScreen = fscreen.fullscreenElement !== null;
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach(cancel => cancel());
    },
    watch: {
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.contact-distributions-by-language {
    display: flex;
    flex-direction: column;
    gap: 10px;

    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .chart-wrapper {
            height: calc(100vh - 200px);
        }
    }

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }
    }

    .chart-wrapper {
        min-height: 300px;
        height: auto;
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
